/**
 * Shared AudioContext singleton.
 *
 * iOS (Safari & Chrome-via-WebKit) limits the number of AudioContext instances
 * a page can have.  Creating a new one in every component / effect leaks
 * resources, eventually causing audio to stop working altogether – which
 * manifests as "student can no longer hear teacher" on low-end iPads.
 *
 * This module exposes a single lazily-created AudioContext that the entire
 * app re-uses, plus helpers for the common iOS "unlock audio" pattern.
 */

const logger = console;

let _ctx: AudioContext | null = null;

/**
 * Return the shared AudioContext, creating it on first call.
 * On iOS the context usually starts in "suspended" state until a user
 * gesture resumes it – call `resumeSharedAudioContext()` from a click /
 * touchstart handler to unblock it.
 */
export function getSharedAudioContext(): AudioContext {
  if (!_ctx) {
    const AudioCtx =
      window.AudioContext || (window as any).webkitAudioContext;
    _ctx = new AudioCtx({
      sampleRate: 44100,
      latencyHint: "interactive",
    });
    logger.log(
      "[SharedAudioContext] Created – state:",
      _ctx.state,
      "sampleRate:",
      _ctx.sampleRate
    );
  }
  return _ctx;
}

/**
 * Resume the shared context if it is suspended (required on iOS).
 * Safe to call multiple times – it's a no-op when already running.
 * Returns `true` when the context is in the "running" state afterwards.
 */
export async function resumeSharedAudioContext(): Promise<boolean> {
  const ctx = getSharedAudioContext();
  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
      logger.log("[SharedAudioContext] Resumed – state:", ctx.state);
    } catch (err) {
      logger.error("[SharedAudioContext] Resume failed:", err);
    }
  }
  return ctx.state === "running";
}

/**
 * Play a silent buffer through the shared context.
 * This is the standard trick to "unlock" audio playback on iOS after a user
 * gesture.  Call from a click / touchstart handler.
 */
export function playSilentBuffer(): void {
  try {
    const ctx = getSharedAudioContext();
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start();
    logger.log("[SharedAudioContext] Silent buffer played");
  } catch (err) {
    logger.error("[SharedAudioContext] Silent buffer failed:", err);
  }
}

/**
 * One-shot helper that resumes the context **and** plays a silent buffer.
 * Designed to be called once from a user-gesture event listener.
 */
export async function unlockAudio(): Promise<void> {
  await resumeSharedAudioContext();
  playSilentBuffer();
}
